import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Car, Search, Upload } from "lucide-react";

export default function Veiculos() {
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/vehicles", { params: q ? { q } : {} });
    setVehicles(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="space-y-5" data-testid="veiculos-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Frota</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Veículos</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-vehicle-btn"><Plus className="w-4 h-4" /> Novo Veículo</Button>
          </DialogTrigger>
          <NovoVeiculoDialog onCreated={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Buscar por VIN, placa, marca, modelo…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="vehicle-search-input" />
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Carregando…</div> :
        vehicles.length === 0 ? <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">Nenhum veículo cadastrado.</div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="vehicles-grid">
          {vehicles.map((v) => (
            <button key={v.id} onClick={() => navigate(`/veiculos/${v.id}`)}
              className="text-left border border-border rounded-md bg-card overflow-hidden hover:-translate-y-0.5 hover:shadow-sm transition-all"
              data-testid={`vehicle-card-${v.id}`}>
              <div className="h-32 bg-secondary relative">
                {v.photo_url ? <img src={v.photo_url} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center"><Car className="w-8 h-8 text-muted-foreground" /></div>}
                {v.plate && <div className="absolute bottom-2 left-2 font-mono text-[10px] px-2 py-0.5 rounded bg-black/70 text-white tracking-wider">{v.plate}</div>}
              </div>
              <div className="p-3">
                <div className="font-display font-semibold text-sm">{v.year} {v.make} {v.model}</div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{v.client_name || "—"}</div>
                <div className="mt-2 text-[11px] text-muted-foreground font-mono truncate">VIN: {v.vin || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      }
    </div>
  );
}

function NovoVeiculoDialog({ onCreated }) {
  const [form, setForm] = useState({ client_id: "", vin: "", plate: "", year: "", make: "", model: "", trim: "", engine: "", fuel: "Gasolina", drivetrain: "", mileage: "", photo_url: "" });
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecione um cliente"); return; }
    if (!form.make || !form.model) { toast.error("Marca e modelo são obrigatórios"); return; }
    setSaving(true);
    try {
      await api.post("/vehicles", {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
      });
      toast.success("Veículo criado");
      onCreated();
    } catch { toast.error("Erro ao criar"); }
    finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Foto enviada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploading(false); }
  };

  return (
    <DialogContent className="sm:max-w-2xl" data-testid="new-vehicle-dialog">
      <DialogHeader><DialogTitle className="font-display tracking-tight">Novo Veículo</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Cliente *</Label>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
            <SelectTrigger data-testid="vehicle-client-select"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Ano</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="vehicle-year-input" /></div>
          <div><Label>Marca *</Label><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="vehicle-make-input" /></div>
          <div><Label>Modelo *</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehicle-model-input" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>VIN</Label><Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} data-testid="vehicle-vin-input" /></div>
          <div><Label>Placa</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="vehicle-plate-input" /></div>
          <div><Label>Milhagem (Mileage)</Label><Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} /></div>
        </div>
        <div>
          <Label>Foto (upload ou URL)</Label>
          <div className="flex gap-2">
            <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" />
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm">
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "…" : "Upload"}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="vehicle-photo-input" />
            </label>
          </div>
          {form.photo_url && <img src={form.photo_url} alt="preview" className="mt-2 h-24 rounded border border-border object-cover" />}
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} data-testid="vehicle-save-btn">{saving ? "Salvando…" : "Salvar Veículo"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
